import { useEffect, useRef, useState } from 'react'
import { useQuoteBuilderApi } from '../api/client'
import { transcribeRecording } from './voice-api'

// A recorded voice message (demo `voice-recorder.ts`): record, transcribe, then send the
// transcript to the chat with the recording attached for playback.

export type VoiceClip = {
  url: string
  transcript: string
  durationSeconds: number
}

export type RecorderStatus = 'idle' | 'starting' | 'recording' | 'transcribing'

const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus']

export function recordingFormat(mimeType: string) {
  if (mimeType.includes('mp4')) return 'mp4'
  if (mimeType.includes('ogg')) return 'ogg'
  return 'webm'
}

async function audioDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('The recording could not be read.'))
    reader.onload = () => resolve(String(reader.result))
    reader.readAsDataURL(blob)
  })
}

export function useVoiceRecorder(onComplete: (clip: VoiceClip) => void) {
  const api = useQuoteBuilderApi()
  const [status, setStatus] = useState<RecorderStatus>('idle')
  const [error, setError] = useState('')
  const [seconds, setSeconds] = useState(0)
  const recorder = useRef<MediaRecorder | null>(null)
  const stream = useRef<MediaStream | null>(null)
  const timer = useRef<number | null>(null)
  const generation = useRef(0)
  const busy = useRef(false)
  const transcription = useRef<AbortController | null>(null)
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  })

  const release = () => {
    if (recorder.current) {
      recorder.current.onstop = null
      recorder.current.onerror = null
      recorder.current.ondataavailable = null
      if (recorder.current.state === 'recording') recorder.current.stop()
    }
    stream.current?.getTracks().forEach((track) => track.stop())
    stream.current = null
    recorder.current = null
    if (timer.current !== null) window.clearInterval(timer.current)
    timer.current = null
  }

  useEffect(
    () => () => {
      generation.current += 1
      busy.current = false
      transcription.current?.abort()
      release()
    },
    [],
  )

  const start = async () => {
    if (busy.current) return
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice recording is unavailable. Use a supported browser on a secure connection, or type your message.')
      return
    }
    busy.current = true
    const attempt = ++generation.current
    setError('')
    setSeconds(0)
    setStatus('starting')
    try {
      const microphone = await navigator.mediaDevices.getUserMedia({
        audio: { autoGainControl: true, echoCancellation: true, noiseSuppression: true },
      })
      if (attempt !== generation.current) {
        microphone.getTracks().forEach((track) => track.stop())
        return
      }
      stream.current = microphone
      const mimeType = MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || ''
      const next = new MediaRecorder(microphone, mimeType ? { mimeType } : undefined)
      const chunks: Blob[] = []
      const startedAt = Date.now()
      recorder.current = next
      next.ondataavailable = (event) => {
        if (event.data.size) chunks.push(event.data)
      }
      next.onerror = () => {
        generation.current += 1
        busy.current = false
        release()
        setStatus('idle')
        setError('Recording stopped unexpectedly. Try again.')
      }
      next.onstop = async () => {
        if (attempt !== generation.current) return
        const recordedSeconds = Math.max(1, (Date.now() - startedAt) / 1000)
        release()
        setStatus('transcribing')
        try {
          const blob = new Blob(chunks, { type: next.mimeType || 'audio/webm' })
          const url = await audioDataUrl(blob)
          if (attempt !== generation.current) return
          const controller = new AbortController()
          transcription.current = controller
          const result = await transcribeRecording(api, url.split(',', 2)[1] || '', recordingFormat(blob.type), controller.signal)
          if (attempt !== generation.current) return
          if (!result.transcript.trim()) {
            setError('No speech was heard. Try recording again.')
            return
          }
          onCompleteRef.current({ url, transcript: result.transcript, durationSeconds: recordedSeconds })
        } catch {
          if (attempt === generation.current) setError('The recording could not be transcribed. Try again or type your message.')
        } finally {
          if (attempt === generation.current) {
            busy.current = false
            transcription.current = null
            setStatus('idle')
          }
        }
      }
      next.start()
      setStatus('recording')
      timer.current = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 250)
    } catch (cause) {
      if (attempt !== generation.current) return
      busy.current = false
      release()
      setStatus('idle')
      setError(
        cause instanceof DOMException && cause.name === 'NotAllowedError'
          ? 'Microphone access is blocked. Allow it in your browser and try again.'
          : 'The microphone could not start. Try again.',
      )
    }
  }

  const stop = () => {
    if (recorder.current?.state === 'recording') recorder.current.stop()
  }

  const cancel = () => {
    generation.current += 1
    busy.current = false
    transcription.current?.abort()
    transcription.current = null
    release()
    setStatus('idle')
    setError('')
    setSeconds(0)
  }

  return { cancel, error, seconds, start, status, stop }
}
